import { Button, Dialog, FormControl, FormControlLabel, InputLabel, MenuItem, Select, Switch, TextField, CircularProgress, Checkbox } from "@mui/material"
import { useEffect, useRef, useState } from "react";
import { useSnackbar } from "notistack";
import AutocompleteField from "elements/AutocompleteField";
import { ApiRequest, normalizePhoneCL } from "GlobalFunctions";
import LoadingButton from "@mui/lab/LoadingButton";
import * as pdfjsLib from "pdfjs-dist";
import { BsFiletypePdf } from "react-icons/bs";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

pdfjsLib.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.js";

const availabilityOptions = [
  'Disponibilidad completa',
  'Disponibilidad AM',
  'Disponibilidad PM',
  'Disponibilidad Fines de semana',
  'Disponibilidad según turno (clínico/hospitalario)',
];
const insuranceOptions = ['Fonasa', 'Particular', 'Reembolsable con Isapre'];

function PdfThumb({ src }: { src: string }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    let cancelled = false;
    setError(false);
    (async () => {
      try {
        const pdf = await (pdfjsLib as any).getDocument({ url: src, withCredentials: false }).promise;
        if (cancelled) return;
        const page = await pdf.getPage(1);
        if (cancelled) return;
        const canvas = canvasRef.current;
        if (!canvas) return;
        const vp = page.getViewport({ scale: 1 });
        const scale = (canvas.parentElement?.clientWidth || 64) / vp.width;
        const scaled = page.getViewport({ scale });
        canvas.width = scaled.width; canvas.height = scaled.height;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;
        await page.render({ canvasContext: ctx, viewport: scaled }).promise;
      } catch { if (!cancelled) setError(true); }
    })();
    return () => { cancelled = true; };
  }, [src]);
  if (error) return <BsFiletypePdf size={32} color="#005F6A" style={{ opacity: 0.7 }} />;
  return <canvas ref={canvasRef} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />;
}



export default function UsersEditor(props : any) {

  /*=============================================
  VARIABLES FROM PROPS
  ============================================= */
  const {
    openEditor,
    setOpenEditor,
    user_id,
    loadList,
  } = props;

  /*SNACKBAR FUNCTION FOR NOTIFICATIONS*/
  const { enqueueSnackbar } = useSnackbar();
  /*============================================= */



  const defaultValues: any = {
    user_id: user_id ? user_id : "null",
    name: "",
    lastname: "",
    email: "",
    password: "",
    role_id: 2,
    phone: "",
    whatsApp: "",
    is_active: true,
    deleted: false,
    image: "",
    nurse_id: null,
    university_degree: "",
    service: "",
    price: "",
    region_id: "",
    locations: [],
    skills: [],
    advanced_wound_care: false,
    online_advice: false,
    about_me: "",
    average_rating: "0.0000",
    locations_labels: [],
    skills_labels: [],
    skills_map: {},
    specialty_prices: {},
    availability: [],
    insurance_types: [],
    is_register_approved: 0,
    home_comuna_id: "",
    superintendencia_registro: "",
    rut: "",
    commission_consent: false,
    commission_consent_date: "",
  };
  
  const [formValues, setFormValues] = useState(defaultValues)
  const [loading, setLoading] = useState(false);

  const [credentials, setCredentials] = useState<any[]>([]);
  const [credTitle, setCredTitle] = useState('');
  const [credFile, setCredFile] = useState<any>(null);
  const [credLoading, setCredLoading] = useState(false);
  const [commissionTerms, setCommissionTerms] = useState('');
  const [skillsList, setSkillsList] = useState<any[]>([]);
  const [acceptingCommission, setAcceptingCommission] = useState(false);

  const loadCredentials = async (nurse_id: any) => {
    await ApiRequest({
      url: '/kinesiologists/credentials',
      method: 'GET',
      query: { nurse_id },
      setResponse: (r: any) => { if (r?.status === 'success') setCredentials(r.data || []); },
    });
  };

  const addCredential = async () => {
    if (!credFile || !credTitle.trim()) { enqueueSnackbar('Agrega título y archivo', { variant: 'warning' }); return; }
    if ((credentials?.length || 0) >= 3) { enqueueSnackbar('Máximo 3 certificados', { variant: 'warning' }); return; }
    setCredLoading(true);
    const fd = new FormData();
    fd.append('formValues', JSON.stringify({ nurse_id: formValues.nurse_id, title: credTitle }));
    fd.append('image', credFile);
    await ApiRequest({
      url: '/kinesiologists/credentials', method: 'POST', formData: true, body: fd,
      setResponse: (r: any) => {
        setCredLoading(false);
        if (r?.status === 'success') {
          enqueueSnackbar('Certificado guardado', { variant: 'success' });
          setCredTitle(''); setCredFile(null);
          loadCredentials(formValues.nurse_id);
        }
      },
    });
  };

  const deleteCredential = async (credential_id: any) => {
    await ApiRequest({
      url: '/kinesiologists/credentials/delete', method: 'POST', body: { credential_id },
      setResponse: (r: any) => { if (r?.status === 'success') loadCredentials(formValues.nurse_id); },
    });
  };

  const acceptCommissionForNurse = async () => {
    setAcceptingCommission(true);
    await ApiRequest({
      url: '/kinesiologists/acceptCommission',
      method: 'POST',
      body: { target_user_id: formValues.user_id },
      setResponse: (r: any) => {
        setAcceptingCommission(false);
        if (r?.status === 'success') {
          enqueueSnackbar('Consentimiento registrado', { variant: 'success' });
          setFormValues({ ...formValues, commission_consent: true, commission_consent_date: new Date().toISOString() });
        } else {
          enqueueSnackbar(r?.message || 'No se pudo registrar', { variant: 'error' });
        }
      },
    });
  };





  useEffect(() => {
    ApiRequest({
      url: '/settings/details',
      method: 'GET',
      setResponse: (r: any) => { if (r.seo?.preferente_terms) setCommissionTerms(r.seo.preferente_terms); }
    });
    ApiRequest({
      url: '/skills/list',
      method: 'GET',
      query: { full_list: true },
      setResponse: (r: any) => { setSkillsList(r.list || []); }
    });
  }, []);

  /*=======================================
  GET USER DETAILS IF USER_ID EXISTS
  =======================================*/
  useEffect(() => {
    if (user_id) {
      ApiRequest({
        url: "/users/details",
        method: "GET",
        query: { user_id },
        setResponse: (response: any) => {
          setFormValues({
            ...defaultValues,
            ...response,
            skills: Array.isArray(response.skills) ? response.skills.map((s: any) => Number(s)) : [],
          });
          if (response.nurse_id) loadCredentials(response.nurse_id);
        },
      });
    }
    else{
      setFormValues(defaultValues);
      setCredentials([]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  } , [user_id])




  const updateSpecialtyPrice = (skill_id: number, price: string) => {
    const specialty_prices = { ...(formValues.specialty_prices || {}) };
    specialty_prices[String(skill_id)] = price;
    setFormValues({ ...formValues, specialty_prices });
  };

  /*=======================================
  HANDLE INPUT CHANGE
  =======================================*/
  const handleInputChange = (e: { target: { name: any; value: any; }; }) => {
    const { name, value } = e.target;
    setFormValues({
      ...formValues,
      [name]: value,
    });
  };


  /*=======================================
  SUBMIT FUNCTION
  =======================================*/
  const handleSubmit = () => {  
    setLoading(true);

    const body = new FormData();
    body.append("formValues", JSON.stringify(formValues));
    console.log("formValues", formValues);


    ApiRequest({
      url: "/users/updateAccount",
      method: "POST",
      formData: true,
      body,
      setResponse: (response: any) => {
        setLoading(false);
        if (response.status === "success") {
          enqueueSnackbar('Usuario guardado con éxito', { variant: 'success' });
          setOpenEditor(false);
          loadList();
        } else {
          enqueueSnackbar(response.message || 'Error al guardar', { variant: 'error' });
        }
      },
      onError: (msg: string) => {
        setLoading(false);
        enqueueSnackbar(msg, { variant: 'error' });
      },
    });
  };






  return (
      <>
        <Dialog 
          open={openEditor}
          onClose={() => setOpenEditor(false)}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <div className="DialogContainer">
              <div className="ModuleWrapper">
                  <div className="boxContainerHeader">
                      <div className="boxContainerTitle">
                          <h2 className="h2_title">Usuario</h2>
                          <span className="h2_subtitle">Editar</span>
                      </div>

                      
                  </div>

                  <div id="userForm">
                    <div className="userForm_right">
                      
                        <div className='input_group'>
                            <h2>Información Personal</h2>
                            <div className='input_group'>
                              <div className='input_row'>
                                <TextField 
                                  id="name"
                                  name="name"
                                  label="Nombres"
                                  type="text"
                                  required
                                  fullWidth
                                  value={formValues.name}
                                  onChange={handleInputChange}
                                />

                                <TextField
                                  id="lastname"
                                  name="lastname"
                                  label="Apellidos"
                                  type="text"
                                  required
                                  fullWidth
                                  value={formValues.lastname}
                                  onChange={handleInputChange}
                                />
                              </div>
                              <div className='input_row'>
                                <TextField
                                  id="phone"
                                  name="phone"
                                  label="Teléfono"
                                  type="text"
                                  required
                                  fullWidth
                                  value={formValues.phone}
                                  onChange={(e) => {
                                    const updates: any = { phone: e.target.value };
                                    if (!formValues.whatsapp) updates.whatsapp = normalizePhoneCL(e.target.value);
                                    setFormValues({ ...formValues, ...updates });
                                  }}
                                  helperText={!formValues.whatsapp ? 'Se usará también como WhatsApp' : ''}
                                />

                                <TextField
                                  id="whatsapp"
                                  name="whatsapp"
                                  label="WhatsApp"
                                  type="text"
                                  fullWidth
                                  value={formValues.whatsapp}
                                  onChange={(e) => setFormValues({ ...formValues, whatsapp: normalizePhoneCL(e.target.value) })}
                                  helperText="9 dígitos, sin +56 ni espacios (se limpia automáticamente)"
                                />
                              </div>

                            </div>

                            <div className='input_group'>
                              <h2>Información de sesión</h2>
                              <div className="input_row">
                                <TextField
                                  id="email"
                                  name="email"
                                  label="Email"
                                  type="text"
                                  required
                                  fullWidth
                                  value={formValues.email}
                                  onChange={handleInputChange}
                                />

                                <TextField
                                  id="password"
                                  name="password"
                                  label="Password"
                                  type="password"
                                  autoComplete="new-password"
                                  required
                                  fullWidth
                                  value={formValues.password}
                                  onChange={handleInputChange}
                                />
                              </div>



                            </div>

                            {formValues.role_id !== 1 && (
                              <div className='input_group'>
                                <h2>Información de Kinesiólogo</h2>
                                <AutocompleteField
                                  label="Región"
                                  endpoint="/locations/regiones/list"
                                  value={formValues.region_id}
                                  onChange={(data: any) => {
                                    setFormValues({ 
                                      ...formValues, 
                                      region_id: data.region_id
                                    });
                                  }}
                                />
                                {formValues.region_id && formValues.region_id !== '' && (
                                  <AutocompleteField
                                    label="Comunas"
                                    endpoint="/locations/comunas/list"
                                    endpoint_params={{ region_id: formValues.region_id }}
                                    multiple={true}
                                    value={formValues.locations}
                                    onChange={(data: any) => {
                                      setFormValues({ ...formValues, locations: data ? data : [] });
                                    }}
                                  />
                                )}
                                <AutocompleteField
                                  label="Especialidades"
                                  endpoint="/skills/list"
                                  multiple={true}
                                  value={formValues.skills}
                                  onChange={(data: any) => {
                                    setFormValues({ ...formValues, skills: data ? data : [] });
                                  }}
                                />
                                <TextField
                                  id="about_me"
                                  name="about_me"
                                  label="Sobre mí"
                                  type="text"
                                  fullWidth
                                  multiline
                                  value={formValues.about_me}
                                  onChange={handleInputChange}
                                />

                                {/* RUT */}
                                <div className='input_row'>
                                  <TextField
                                    label="RUT"
                                    value={formValues.rut}
                                    onChange={(e) => setFormValues({ ...formValues, rut: e.target.value })}
                                    fullWidth
                                  />
                                </div>

                                {/* Domicilio y registro */}
                                <div className='input_row'>
                                  <AutocompleteField
                                    label="Comuna de residencia"
                                    endpoint="/locations/comunas/list"
                                    value={formValues.home_comuna_id}
                                    onChange={(data: any) => setFormValues({ ...formValues, home_comuna_id: data.comuna_id })}
                                  />
                                  <TextField
                                    label="Registro de la Superintendencia de Salud"
                                    value={formValues.superintendencia_registro}
                                    onChange={(e) => setFormValues({ ...formValues, superintendencia_registro: e.target.value })}
                                    fullWidth
                                  />
                                </div>

                                {/* Atenciones y precios */}
                                {(formValues.skills || []).length > 0 && (
                                  <div className='input_group'>
                                    <h2>Atenciones y Valores Referenciales</h2>
                                    {(formValues.skills || []).map((skill_id: number, i: number, arr: number[]) => {
                                      const label = skillsList.find((s: any) => s.skill_id === skill_id)?.skill || `Especialidad #${skill_id}`;
                                      const isLast = i === arr.length - 1;
                                      return (
                                        <div key={skill_id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', paddingBottom: isLast ? 0 : '10px', marginBottom: isLast ? 0 : '10px', borderBottom: isLast ? 'none' : '1px solid #f0f0f0' }}>
                                          <span style={{ fontWeight: 600, fontSize: 14 }}>{label}</span>
                                          <TextField
                                            label="Valor referencial ($)"
                                            value={formValues.specialty_prices?.[String(skill_id)] || ''}
                                            onChange={(e) => updateSpecialtyPrice(skill_id, e.target.value.replace(/[^0-9]/g, ''))}
                                            size="small"
                                            style={{ width: '200px' }}
                                            inputProps={{ inputMode: 'numeric' }}
                                          />
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}

                                {/* Disponibilidad interna */}
                                <div className='input_group'>
                                  <h2>Disponibilidad (uso interno)</h2>
                                  {availabilityOptions.map((option) => (
                                    <FormControlLabel
                                      key={option}
                                      control={
                                        <Checkbox
                                          checked={(formValues.availability || []).includes(option)}
                                          onChange={(e) => {
                                            const availability = e.target.checked
                                              ? [...(formValues.availability || []), option]
                                              : (formValues.availability || []).filter((x: string) => x !== option);
                                            setFormValues({ ...formValues, availability });
                                          }}
                                        />
                                      }
                                      label={option}
                                      style={{ display: 'flex' }}
                                    />
                                  ))}
                                </div>

                                {/* Tipo de atención / pago */}
                                <div className='input_group'>
                                  <h2>Tipo de atención / pago</h2>
                                  <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                                    {insuranceOptions.map((option) => (
                                      <FormControlLabel
                                        key={option}
                                        control={
                                          <Checkbox
                                            checked={(formValues.insurance_types || []).includes(option)}
                                            onChange={(e) => {
                                              const insurance_types = e.target.checked
                                                ? [...(formValues.insurance_types || []), option]
                                                : (formValues.insurance_types || []).filter((x: string) => x !== option);
                                              setFormValues({ ...formValues, insurance_types });
                                            }}
                                          />
                                        }
                                        label={option}
                                      />
                                    ))}
                                  </div>
                                </div>

                                <FormControlLabel
                                  control={
                                    <Switch
                                      checked={formValues.advanced_wound_care}
                                      onChange={(e) => setFormValues({ ...formValues, advanced_wound_care: e.target.checked })}
                                    />
                                  }
                                  label="Kinesiología a Domicilio"
                                />
                                <FormControlLabel
                                  control={
                                    <Switch
                                      checked={formValues.online_advice}
                                      onChange={(e) => setFormValues({ ...formValues, online_advice: e.target.checked })}
                                    />
                                  }
                                  label="Kinesiología en Consulta"
                                />

                                {/* Comisión por derivación */}
                                {formValues.nurse_id && (
                                <div className='input_group'>
                                  <h2 style={{ margin: '0 0 8px 0' }}>Comisión por Derivación ($10.000)</h2>
                                  {formValues.commission_consent ? (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#0A7715' }}>
                                      <CheckCircleIcon fontSize="small" />
                                      <span>
                                        El kinesiólogo aceptó el cobro de $10.000 por derivación
                                        {formValues.commission_consent_date ? ` el ${String(formValues.commission_consent_date).slice(0, 10)}` : ''}.
                                      </span>
                                    </div>
                                  ) : (
                                    <>
                                      <p style={{ margin: '0 0 10px 0', fontSize: '13px', color: '#555' }}>
                                        El kinesiólogo aún no ha aceptado el cobro de $10.000 por derivación. Su perfil solo permite
                                        mensajería interna. Puedes registrar la aceptación en su nombre si la confirmó por otro medio.
                                      </p>
                                      {commissionTerms && (
                                        <div style={{ padding: '14px 16px', background: '#fffbeb', border: '1px solid #fbbf24', borderRadius: '8px', fontSize: '13px', color: '#78350f', lineHeight: 1.6, marginBottom: '10px' }}>
                                          <div dangerouslySetInnerHTML={{ __html: commissionTerms }} />
                                        </div>
                                      )}
                                      <LoadingButton size="small" variant="contained" onClick={acceptCommissionForNurse} loading={acceptingCommission}>
                                        Registrar aceptación
                                      </LoadingButton>
                                    </>
                                  )}
                                </div>
                                )}

                                {/* Certificados */}
                                <div className='input_group' style={{ border: '1px solid #e0e0e0', borderRadius: 10, padding: 16, background: '#fafafa' }}>
                                  <h2 style={{ margin: '0 0 8px 0' }}>Certificados y Especializaciones</h2>
                                  <p style={{ margin: '0 0 12px 0', fontSize: 13, color: '#666' }}>Máximo 3 certificados (imagen o PDF).</p>

                                  {formValues.nurse_id ? (
                                    <>
                                      <div className='input_row'>
                                        <TextField
                                          label="Título del certificado"
                                          value={credTitle}
                                          onChange={(e) => setCredTitle(e.target.value)}
                                          fullWidth size="small"
                                        />
                                        <Button variant="outlined" component="label" size="small" sx={{ minWidth: 180, height: 40 }}>
                                          {credFile?.name ? credFile.name.slice(0, 18) + (credFile.name.length > 18 ? '…' : '') : 'Seleccionar archivo'}
                                          <input type="file" hidden accept="image/*,.pdf,application/pdf"
                                            onChange={(e: any) => { const f = e?.target?.files?.[0]; if (f) setCredFile(f); }} />
                                        </Button>
                                        <LoadingButton size="small" variant="contained" onClick={addCredential} loading={credLoading}
                                          disabled={(credentials?.length || 0) >= 3 || credLoading}>
                                          Agregar
                                        </LoadingButton>
                                        <span style={{ fontSize: 12, color: '#999', whiteSpace: 'nowrap' }}>{credentials?.length || 0}/3</span>
                                      </div>

                                      {credentials && credentials.length > 0 && (
                                        <div style={{ marginTop: 12, display: 'grid', gap: 10 }}>
                                          {credentials.map((c: any) => (
                                            <div key={c.credential_id} style={{ display: 'flex', gap: 12, alignItems: 'center', border: '1px solid #e6e6e6', borderRadius: 10, padding: 10, background: '#fff' }}>
                                              <div style={{ width: 64, height: 64, borderRadius: 8, overflow: 'hidden', background: '#f5f5f5', flex: '0 0 64px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                {c.image && (c.image.toLowerCase().endsWith('.pdf')
                                                  ? <a href={c.image} target="_blank" rel="noreferrer" style={{ display: 'block', width: '100%', height: '100%' }}><PdfThumb src={c.image} /></a>
                                                  : <a href={c.image} target="_blank" rel="noreferrer" style={{ display: 'block', width: '100%', height: '100%' }}><img src={c.image} alt={c.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /></a>
                                                )}
                                              </div>
                                              <div style={{ flex: 1 }}>
                                                <div style={{ fontWeight: 600 }}>{c.title}</div>
                                                <div style={{ fontSize: 12, color: '#999' }}>{c.created_at || ''}</div>
                                              </div>
                                              <Button variant="text" color="error" size="small" onClick={() => deleteCredential(c.credential_id)}>Eliminar</Button>
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                    </>
                                  ) : (
                                    <p style={{ fontSize: 13, color: '#aaa' }}>Guarda primero el perfil de la kinesiólogo para subir certificados.</p>
                                  )}
                                </div>

                              </div>
                            )}

                          



                          <div className='input_group'>
                            <h2>Estado de Registro</h2>
                            <FormControl fullWidth>
                              <InputLabel id="is_register_approved-label">Estado de Registro</InputLabel>
                              <Select
                                labelId="is_register_approved-label"
                                id="is_register_approved"
                                name="is_register_approved"
                                value={formValues.is_register_approved}
                                label="Estado de Registro"
                                onChange={handleInputChange}
                              >
                                <MenuItem value={0}>Pendiente</MenuItem>
                                <MenuItem value={1}>Aprobado</MenuItem>
                                <MenuItem value={2}>Rechazado</MenuItem>
                              </Select>
                            </FormControl>

                            <FormControlLabel
                              control={
                                <Switch
                                  checked={formValues.is_active}
                                  onChange={(e) => setFormValues({ ...formValues, is_active: e.target.checked })}
                                />
                              }
                              label="Perfil Activo"
                            />
                          </div>



                        </div>


                      
                    </div>


                    

                </div>
              </div>
          </div>

          <div className="dialogButtons">
            <Button
                onClick={() => setOpenEditor(false)}
                variant="outlined"
                className="button_2"
                disabled={loading}
            >
              Cancelar
            </Button>

            <Button 
              onClick={handleSubmit} 
              variant="contained" 
              className="button_1"
              disabled={loading}
              startIcon={loading && <CircularProgress size={20} />}
            >
              {loading ? 'Guardando...' : 'Guardar'}
            </Button>
          </div>

        </Dialog>
      </>
  );
}
